import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatDateForFile = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const sanitizeFilePart = (value) => (value || '')
  .toString()
  .trim()
  .replace(/\s+/g, '_')
  .replace(/[^\w.-]/g, '')
  .slice(0, 48);

const formatDateTime = (language = 'tr-TR') => new Date().toLocaleString(language);

const COVER_TEXT = {
  'tr-TR': {
    reportTitle: 'Trend Intelligence',
    reportSubtitle: 'CFO Sunum Paketi',
    company: 'Sirket',
    period: 'Donem',
    generatedAt: 'Olusturma Zamani',
    confidentiality: 'Gizli - Sadece Yetkili Yonetim Kullanimi Icindir',
    sectionTitle: 'Yonetici Ozeti',
    sectionBody: 'Bu dokuman, secili donem araliginda rasyo momentumu, esik ihlalleri ve kritik hareketleri yonetici seviyesi sunum formatinda bir araya getirir.'
  },
  'en-US': {
    reportTitle: 'Trend Intelligence',
    reportSubtitle: 'CFO Presentation Pack',
    company: 'Company',
    period: 'Period',
    generatedAt: 'Generated At',
    confidentiality: 'Confidential - For Authorized Management Use Only',
    sectionTitle: 'Executive Summary',
    sectionBody: 'This document consolidates ratio momentum, threshold violations and critical movements across the selected period range in an executive presentation format.'
  }
};

const t = (language, key) => COVER_TEXT[language]?.[key] || COVER_TEXT['tr-TR'][key] || key;

export async function exportRatioTrendPdf({
  element,
  companyName = '',
  periodLabel = '',
  language = 'tr-TR'
}) {
  if (!element) {
    throw new Error(language.startsWith('tr') ? 'PDF alani bulunamadi.' : 'PDF source area not found.');
  }

  const scale = clamp(window.devicePixelRatio || 1, 1.5, 2.5);
  const canvas = await html2canvas(element, {
    backgroundColor: '#0f1b33',
    scale,
    useCORS: true,
    allowTaint: false,
    logging: false,
    width: element.scrollWidth,
    height: element.scrollHeight,
    windowWidth: Math.max(element.scrollWidth, window.innerWidth),
    windowHeight: Math.max(element.scrollHeight, window.innerHeight),
    scrollX: 0,
    scrollY: -window.scrollY,
    onclone: (clonedDocument) => {
      clonedDocument.querySelectorAll('[data-export-hidden="true"]').forEach((node) => node.remove());
    }
  });

  const imageData = canvas.toDataURL('image/png', 1);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const nowLabel = formatDateTime(language);

  doc.setFillColor(11, 18, 32);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(22, 37, 64);
  doc.roundedRect(10, 10, pageWidth - 20, pageHeight - 20, 4, 4, 'F');

  doc.setFillColor(39, 72, 130);
  doc.rect(10, 10, pageWidth - 20, 22, 'F');

  doc.setTextColor(240, 246, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(20);
  doc.text(t(language, 'reportTitle'), 16, 24);
  doc.setFontSize(11);
  doc.setFont(TURKISH_FONT, 'normal');
  doc.text(t(language, 'reportSubtitle'), 16, 30);

  doc.setDrawColor(73, 119, 190);
  doc.setLineWidth(0.6);
  doc.line(16, 42, pageWidth - 16, 42);

  doc.setTextColor(214, 228, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(12);
  doc.text(t(language, 'sectionTitle'), 16, 52);
  doc.setFont(TURKISH_FONT, 'normal');
  doc.setFontSize(10.5);
  doc.text(doc.splitTextToSize(t(language, 'sectionBody'), 110), 16, 59);

  doc.setFillColor(19, 32, 56);
  doc.roundedRect(pageWidth - 92, 48, 76, 55, 3, 3, 'F');
  doc.setDrawColor(54, 87, 145);
  doc.roundedRect(pageWidth - 92, 48, 76, 55, 3, 3, 'S');

  doc.setTextColor(232, 241, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(10);
  doc.text(`${t(language, 'company')}:`, pageWidth - 88, 58);
  doc.text(`${t(language, 'period')}:`, pageWidth - 88, 72);
  doc.text(`${t(language, 'generatedAt')}:`, pageWidth - 88, 86);

  doc.setFont(TURKISH_FONT, 'normal');
  doc.setFontSize(9.5);
  doc.text(doc.splitTextToSize(companyName || '-', 52), pageWidth - 88, 63);
  doc.text(doc.splitTextToSize(periodLabel || '-', 52), pageWidth - 88, 77);
  doc.text(doc.splitTextToSize(nowLabel, 52), pageWidth - 88, 91);

  doc.setTextColor(164, 185, 222);
  doc.setFontSize(8.8);
  doc.text(t(language, 'confidentiality'), 16, pageHeight - 14);

  doc.addPage('a4', 'landscape');

  const margin = 6;
  const renderWidth = pageWidth - (margin * 2);
  const renderHeight = (canvas.height * renderWidth) / canvas.width;
  const printableHeight = pageHeight - (margin * 2);
  const pageStep = Math.max(1, printableHeight);

  let offsetY = 0;
  let pageIndex = 0;
  while (offsetY < renderHeight - 0.5) {
    if (pageIndex > 0) {
      doc.addPage('a4', 'landscape');
    }
    doc.setFillColor(11, 18, 32);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    const drawY = margin - offsetY;
    doc.addImage(imageData, 'PNG', margin, drawY, renderWidth, renderHeight, undefined, 'FAST');

    offsetY += pageStep;
    pageIndex += 1;
  }

  const safeCompany = sanitizeFilePart(companyName);
  const safePeriod = sanitizeFilePart(periodLabel);
  const safeTitle = sanitizeFilePart(t(language, 'reportTitle')) || 'ratio_trend_report';
  const datePart = formatDateForFile(new Date());
  const filenameParts = [safeTitle, safeCompany, safePeriod, datePart].filter(Boolean);
  doc.save(`${filenameParts.join('_')}.pdf`);
}
